<template>
  <v-btn
    :loading="isLoading"
    :disabled="isLoading || !hasSections"
    :ripple="false"
    color="secondary"
    @click="download"
  >
    <v-icon left>
      {{ icons.mdiFileExcel }}
    </v-icon>
    XLSXダウンロード
  </v-btn>
</template>

<script>
import { mdiFileExcel } from '@mdi/js'
import { ref, computed, getCurrentInstance } from '@vue/composition-api'
import * as XLSX from 'xlsx/xlsx.mjs'
import pLimit from 'p-limit'
import ExpenseApi from '@/api/admin/Expense'
import BenefitApi from '@/api/admin/Benefit'
import TableSummaryApi from '@/api/admin/TableSummary'
import CashRegisterTransactionApi from '@/api/waiter/CashRegisterTransaction'
import CashRegisterTransactionSummaryApi from '@/api/waiter/CashRegisterTransactionSummary'
import EodClosingAggregationApi from '@/api/v2/eod-closing/Aggregation'
import useExpensesAndBenefitsDataTable from '@/views/composable/data-table/useExpensesAndBenefitsDataTable'
import useTableResultOverviewsDataTable from '@/views/composable/data-table/useTableResultOverviewsDataTable'
import useCashRegisterTransactionsDataTable from '@/views/composable/data-table/useCashRegisterTransactionsDataTable'
import useCashRegisterTransactionSummariesDataTable from '@/views/composable/data-table/useCashRegisterTransactionSummariesDataTable'
import useCompInit from '@/views/composable/useCompInit'
import useCurrentData from '@/views/composable/useCurrentData'
import { useAggregationJobs } from '@/views/v2-temp/report/aggregation-export/composables/useAggregationJobs'
import {
  buildWorksheetFromAggregation,
  createMetaSheet,
  generateUniqueSheetName,
  hashId,
} from '@/views/v2-temp/report/aggregation-export/composables/useExcel'
import { makeRangeKey } from '@/utils/async'
import { handleApiError } from '@/utils/errorHandler'

export default {
  props: {
    date: {
      type: String,
      required: true,
    },
    sections: {
      type: Array,
      required: true,
    },
  },
  setup(props) {
    const vm = getCurrentInstance().proxy
    const { isLoading, initWith } = useCompInit()
    const hasSections = computed(() => props.sections.length > 0)
    const { getStatus, statuses, results } = useAggregationJobs()
    const { currentClub } = useCurrentData()

    const expenses = ref([])
    const benefits = ref([])
    const tableResultOverviews = ref([])
    const dynamicTableResultOverviewHeaders = ref([])
    const cashRegisterTransactions = ref([])
    const cashRegisterTransactionSummaries = ref([])

    const expensesAndBenefitsDataTable = useExpensesAndBenefitsDataTable({ expenses, benefits })
    const tableResultOverviewsDataTable = useTableResultOverviewsDataTable({ tableResultOverviews, dynamicTableResultOverviewHeaders })
    const {
      buildAllTransactionsXlsxData,
      buildDepositTransactionsXlsxData,
      buildWithdrawTransactionsXlsxData,
    } = useCashRegisterTransactionsDataTable(cashRegisterTransactions)
    const cashRegisterTxSummariesDataTable = useCashRegisterTransactionSummariesDataTable(cashRegisterTransactionSummaries)

    const buildSectionRows = section => {
      const row = { 営業日: props.date }
      const nameCount = {}
      section.metrics.forEach(metric => {
        nameCount[metric.name] = (nameCount[metric.name] || 0) + 1
      })
      const nameIndex = {}
      section.metrics.forEach(metric => {
        const key = nameCount[metric.name] > 1
          ? `${metric.name}(${(nameIndex[metric.name] = (nameIndex[metric.name] || 0) + 1)})`
          : metric.name
        row[key] = metric.total != null ? Number(metric.total) : ''
      })

      return [row]
    }

    const getExpenses = async () => {
      const res = await ExpenseApi.getExpenses({ date: props.date, interval: 0, endDate: '' })
      if (res?.status === 200) expenses.value = res.data.expenses.data
    }

    const getBenefits = async () => {
      const res = await BenefitApi.getBenefits({ date: props.date, interval: 0, endDate: '' })
      if (res?.status === 200) benefits.value = res.data.benefits.data
    }

    const getTableResultOverviews = async () => {
      const res = await TableSummaryApi.getOverviews({ date: props.date, interval: 0, endDate: '' })
      if (res?.status === 200) {
        tableResultOverviews.value = res.data.overviews
        dynamicTableResultOverviewHeaders.value = res.data.dynamicHeaders
      }
    }

    const getCashRegisterTransactions = async () => {
      const res = await CashRegisterTransactionApi.getCashRegisterTransactions({ date: props.date, interval: 0, endDate: '' })
      if (res?.status === 200) cashRegisterTransactions.value = res.data.cashRegisterTransactions.data
    }

    const getCashRegisterTransactionSummaries = async () => {
      const res = await CashRegisterTransactionSummaryApi.getCashRegisterTransactionSummaries({ date: props.date, interval: 0, endDate: '' })
      if (res?.status === 200) cashRegisterTransactionSummaries.value = res.data.cashRegisterTransactionSummaries
    }

    // 既に集計済み（status='completed' かつ結果あり）の日計集計のみシート化する。
    // 未集計は enqueue / ポーリングをせずスキップ（ユーザーは事前にクロージング>日計で集計する運用）。
    // シート並びを決定論的にするため、並列で取得 → 元順で順次 append の 2 フェーズで処理する。
    const getClosingAggregationSheets = async wb => {
      const metaData = []
      let res
      try {
        res = await EodClosingAggregationApi.getAggregations(props.date)
      } catch (e) {
        handleApiError(e, { $toast: vm.$toast })

        return metaData
      }
      const entries = res.data.data.data.map(r => ({
        id: r.attributes.id,
        name: r.attributes.name,
      }))
      const limit = pLimit(3)

      // フェーズA: 並列で status 取得 + WorkSheet 生成（Promise.all は entries 元順で結果を返す）
      const sheets = await Promise.all(entries.map(({ id, name }) => limit(async () => {
        const range = [props.date, props.date]
        await getStatus(id, range)
        const key = makeRangeKey(id, range)
        if (statuses[key] !== 'completed' || !results[key]) return null

        return {
          id,
          name,
          ws: buildWorksheetFromAggregation(results[key]),
        }
      })))

      // フェーズB: entries 元順で順次 append（毎回同じ並びになる）
      sheets.forEach(s => {
        if (!s) return
        const sheetName = generateUniqueSheetName(s.name, wb.SheetNames)
        XLSX.utils.book_append_sheet(wb, s.ws, sheetName)
        metaData.push({
          uid: hashId(s.id, currentClub.value?.slug ?? ''),
          sheetName,
        })
      })

      return metaData
    }

    const writeXlsx = async () => {
      const wb = XLSX.utils.book_new()

      props.sections.forEach(section => {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(buildSectionRows(section)), section.label)
      })
      // 日報βのエクセルでは「タイプ」列の「諸費」表記を「経費」に変更する（共通ロジックには影響させない）。
      const expensesAndBenefitsRows = expensesAndBenefitsDataTable.buildXlsxData()
        .map(row => (row['タイプ'] === '諸費' ? { ...row, タイプ: '経費' } : row))
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(expensesAndBenefitsRows), '諸入費')
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(tableResultOverviewsDataTable.buildXlsxData()), '卓概要(リスト)')
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(cashRegisterTxSummariesDataTable.buildXlsxData()), 'レジ金Tx(サマリ)')
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(buildAllTransactionsXlsxData()), 'レジ金Tx(ALL)')
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(buildDepositTransactionsXlsxData()), 'レジ金Tx(入金)')
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(buildWithdrawTransactionsXlsxData()), 'レジ金Tx(出金)')

      const metaData = await getClosingAggregationSheets(wb)
      if (metaData.length > 0) {
        XLSX.utils.book_append_sheet(wb, createMetaSheet(metaData), '____meta____')
        wb.SheetNames.unshift(wb.SheetNames.pop())
      }

      XLSX.writeFile(wb, `日報収支_${props.date}.xlsx`)
    }

    const download = async () => {
      await initWith([
        getExpenses(),
        getBenefits(),
        getTableResultOverviews(),
        getCashRegisterTransactions(),
        getCashRegisterTransactionSummaries(),
      ])
      await writeXlsx()
    }

    return {
      isLoading,
      hasSections,
      download,
      icons: { mdiFileExcel },
    }
  },
}
</script>
